<template>
  <UIModal :show="showModal" :width="680">
    <template #header>
      <div v-if="data?.isSaving"
        class="mb-4 flex flex-row items-center gap-3 rounded-2xl bg-warning-50 px-[12px] py-[4px]"
        id="saving-calendar-loader">
        <Loading01Icon class="h-4 w-4 text-warning-500" />
        <div class="text-[13px] font-medium text-warning-700">
          {{ data?.loadingMessage }}
        </div>
      </div>
      <UIModalHeader id="calendarGroupCreateModalHeader" type="success" :title="data?.source === SHARE_SOURCE.GROUP
        ? $t('settings.calendar.share_group')
        : $t('settings.calendar.share_calendar')
        " :description="data?.source === SHARE_SOURCE.GROUP
          ? $t('settings.calendar.group_name') + ' : ' + data?.name
          : ''
          " @close="handleCancel" />
      <div v-if="data.source === SHARE_SOURCE.CALENDAR"
        class="mb-[20px] mt-3 flex flex-row space-x-5 text-sm font-normal text-gray-600">
        <div>
          <ClockIcon class="mb-1 h-4 w-4" />
          <span class="p-2"> {{ data?.slotDuration + ' mins' }}</span>
        </div>
        <div>
          <CalendarIcon class="mb-1 h-4 w-4" />
          <span class="p-2">{{ calendarType }}</span>
        </div>
      </div>
      <hr class="mb-[24px] mt-2 border-t-gray-200" />
    </template>
    <div class="!-mt-3">
      <UITabs v-model:value="tabType" type="line" class="p-2">
        <!-- 1 Scheduling link tab -->
        <UITabPane name="scheduling-link" :tab="$t('settings.calendar.scheduling_link')">
          <div class="mt-4">
            <SchedulingLink :data="data" />
          </div>
        </UITabPane>

        <!-- 2 Scheduling link tab -->
        <UITabPane v-if="data?.showOneTimeLink" name="one-time-scheduling-link"
          :tab="$t('settings.calendar.one_time_link')">
          <div class="mt-4">
            <OneTimeLink :data="data" />
          </div>
        </UITabPane>

        <!-- 3 Scheduling link tab -->
        <UITabPane name="embed-code" :tab="$t('settings.calendar.embed_code')">
          <div class="mt-4">
            <EmbedCode :data="data" />
          </div>
        </UITabPane>
      </UITabs>
    </div>
  </UIModal>
</template>

<script setup lang="ts">
import {
  CalendarIcon,
  ClockIcon,
  Loading01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UIModal, UIModalHeader, UITabPane, UITabs } from '@gohighlevel/ghl-ui'
import { PropType, computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ICalendarShare, SHARE_SOURCE } from '../../types'
import EmbedCode from './CalendarShare/EmbedCode.vue'
import OneTimeLink from './CalendarShare/OneTimeLink.vue'
import SchedulingLink from './CalendarShare/SchedulingLink.vue'

const { t } = useI18n()
const emit = defineEmits(['closeModal'])
const props = defineProps({
  showModal: Boolean,
  data: {
    type: Object as PropType<ICalendarShare>,
    default: null,
  },
})

const tabType = ref('scheduling-link')
const calendarType = computed(() => {
  return props.data?.calendarType === 'event'
    ? t('settings.calendar.event')
    : props.data?.calendarType === 'class_booking'
      ? t('settings.calendar.class_booking')
      : props.data?.calendarType === 'collective'
        ? $t('labels.collective_booking')
        : props.data?.calendarType === 'service_booking'
          ? $t('labels.service_booking')
          : props.data?.calendarType === 'personal'
            ? t('settings.calendar.personal_booking')
            : t('settings.calendar.round_robin')
})
const handleCancel = () => {
  tabType.value = 'scheduling-link'
  emit('closeModal')
}
</script>

<style scoped>
#saving-calendar-loader {
  font-family: 'Inter';
}

#saving-calendar-loader svg {
  animation: rotation 2s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(359deg);
  }
}
</style>
